
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { HarvestFormValues } from './schema';

interface DateFieldProps {
  form: UseFormReturn<HarvestFormValues>;
  name: 'expected_date' | 'actual_date' | 'starting_date' | 'ending_date';
  label: string;
  disabled?: boolean;
}

const DateField: React.FC<DateFieldProps> = ({ form, name, label, disabled = false }) => {
  // Map alternative field names to standard fields for display
  const displayName = name === 'starting_date' ? 'expected_date' : 
                      name === 'ending_date' ? 'ending_date' : name;
  
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex flex-col">
          <FormLabel>{label}</FormLabel>
          <Popover>
            <PopoverTrigger asChild>
              <FormControl>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full pl-3 text-left font-normal",
                    !field.value && "text-muted-foreground"
                  )}
                  disabled={disabled}
                >
                  {field.value ? (
                    format(new Date(field.value), "PPP")
                  ) : (
                    <span>Select a date</span>
                  )}
                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                </Button>
              </FormControl>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={field.value ? new Date(field.value) : undefined}
                onSelect={(date) => {
                  if (date) {
                    const formattedDate = format(date, "yyyy-MM-dd");
                    field.onChange(formattedDate);
                    
                    // If this is starting_date, also update expected_date
                    if (name === 'starting_date') {
                      form.setValue('expected_date', formattedDate);
                    }
                  } else {
                    field.onChange(null);
                    
                    // Clear corresponding field if necessary
                    if (name === 'starting_date') {
                      form.setValue('expected_date', null);
                    }
                  }
                }}
                disabled={disabled}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export default DateField;
